import type { AdminWorkspace, PublicWorkspace } from '../types'
import { STANDARD_CASE_STUDY_ASSETS, STANDARD_PRODUCT_ASSETS, STANDARD_RESOURCE_ASSETS, STANDARD_WHATS_NEW_ASSETS } from './standard-content'

// Fictional Customer Success visual reference. No real client data: names,
// dates, metrics, and calls are all invented for design review.
export const SUCCESS_DEMO_PUBLIC_ID = 'success-preview'

const publicWorkspace: PublicWorkspace = {
  id: '9f4c2a71-55aa-4b7e-9c3d-successdemo01',
  publicId: SUCCESS_DEMO_PUBLIC_ID,
  accountName: 'Summit State University',
  accountLogoUrl: null,
  customerSegment: 'university',
  phase: 'customer_success',
  pricingModel: null,
  pricingModes: 'auto',
  firstCallRecorded: false,
  overview: 'Everything about our partnership in one place: your success plan, call recordings, product updates, and resources.',
  recap: 'Summit State launched Prentus to 4,200 students in January. Activation is climbing ahead of plan and the advising team now runs weekly review queues in the platform. Current focus is lifting student activation in the College of Business ahead of the spring career fair.',
  currentNextStep: 'Lift College of Business student activation ahead of the spring career fair.',
  precallOnepager: null,
  successContent: {
    summary: {
      contractStart: '2026-01-15',
      contractEnd: '2027-01-14',
      customUrl: 'careers.summitstate.edu',
      licenses: 4200,
      addOns: ['SSO', 'Custom Domain', 'Standard Job Scraper', 'AI Mock Interview'],
    },
    metrics: [
      { id: 'metric-activation', label: 'Student activation', target: '80% by June', current: '62%', status: 'on_track' },
      { id: 'metric-jobseeker', label: 'College of Business activation', target: '50% by June', current: '31%', status: 'at_risk' },
      { id: 'metric-outcomes', label: 'Verified outcomes reported', target: '300 by December', current: '112', status: 'on_track' },
    ],
  },
  projects: [
    {
      id: 'project-expansion',
      name: 'College of Business expansion',
      description: 'Bring the College of Business onto Prentus ahead of the spring career fair, with advisors trained and student activation as the headline goal.',
      status: 'in_progress',
      visibility: 'published',
      tasks: [
        { id: 'ptask-1', title: 'Confirm advisor roster and caseload split', ownerName: 'Marcus', dueDate: '2026-08-28', status: 'complete' },
        { id: 'ptask-2', title: 'Advisor training session', ownerName: 'Rod', dueDate: '2026-09-04', status: 'in_progress' },
        { id: 'ptask-3', title: 'Invite College of Business students', ownerName: 'Dana', dueDate: '2026-09-11', status: 'open' },
        { id: 'ptask-4', title: 'Career fair activation push', ownerName: 'Summit State + Prentus', dueDate: '2026-09-25', status: 'open' },
      ],
    },
  ],
  renewal: {
    visibility: 'published',
    headline: '2027 Renewal',
    summary: 'Our first year together moved Summit State from spreadsheet-based career tracking to one system with defensible outcomes data. This proposal continues that work and adds the licenses the College of Business expansion needs.',
    current: {
      termLabel: 'Current term',
      startDate: '2026-01-15',
      endDate: '2027-01-14',
      licenses: 4200,
      perLicenseCost: 27,
      totalCost: 113400,
      billingFrequency: 'Annual',
    },
    highlights: [
      '800 additional licenses to cover the College of Business rollout.',
      'Per-license rate drops from $27 to $25 at the higher volume tier.',
      'All current add-ons carry forward: SSO, custom domain, job scraper, and AI mock interviews.',
    ],
    options: [
      {
        id: 'option-1yr',
        name: '1-year renewal',
        description: 'Same structure as this year, with the licenses the College of Business expansion needs.',
        recommended: false,
        termStart: '2027-01-15',
        termEnd: '2028-01-14',
        billingFrequency: 'Annual',
        licenseLabel: 'Active jobseeker licenses',
        licenseQuantity: 5000,
        licenseUnitPrice: 27,
        addOns: [
          { id: 'a1-sso', name: 'SSO', description: 'Single sign-on through the campus identity provider.', quantity: 5000, unitPrice: 1.5, unitLabel: '/license' },
          { id: 'a1-domain', name: 'Custom Domain', description: 'careers.summitstate.edu, maintained by Prentus.', quantity: 1, unitPrice: 2400, unitLabel: null },
        ],
        discountEnabled: false,
        discountLabel: '',
        discountAmount: null,
      },
      {
        id: 'option-2yr',
        name: '2-year renewal',
        description: 'Locks the rate for two years and adds AI Mock Interviews for every student.',
        recommended: true,
        termStart: '2027-01-15',
        termEnd: '2029-01-14',
        billingFrequency: 'Annual',
        licenseLabel: 'Active jobseeker licenses',
        licenseQuantity: 5000,
        licenseUnitPrice: 27,
        addOns: [
          { id: 'a2-sso', name: 'SSO', description: 'Single sign-on through the campus identity provider.', quantity: 5000, unitPrice: 1.5, unitLabel: '/license' },
          { id: 'a2-domain', name: 'Custom Domain', description: 'careers.summitstate.edu, maintained by Prentus.', quantity: 1, unitPrice: 2400, unitLabel: null },
          { id: 'a2-interviews', name: 'AI Mock Interviews', description: 'Unlimited voice practice interviews with instant feedback.', quantity: 5000, unitPrice: 2, unitLabel: '/license' },
        ],
        discountEnabled: true,
        discountLabel: 'Two-year commitment discount',
        discountAmount: 15000,
      },
    ],
  },
  orderForms: [
    {
      id: 'order-form-2yr',
      number: 'OF-20260915-DEMO',
      title: 'Prentus <> Summit State University',
      status: 'sent',
      createdAt: '2026-09-15T15:00:00.000Z',
      updatedAt: '2026-09-15T15:00:00.000Z',
      sourceOptionId: 'option-2yr',
      validUntil: '2026-10-15',
      customer: {
        legalName: 'Summit State University',
        website: 'summitstate.edu',
        address: '',
        contactName: 'Dana Whitfield',
        contactTitle: 'Vice President of Student Affairs',
        contactEmail: 'dana.whitfield@summitstate.edu',
        billingEmail: 'accountspayable@summitstate.edu',
      },
      prentusContact: { name: 'Rod Danan', title: 'CEO', email: 'rod.danan@prentus.com' },
      preparedBy: { name: 'Rod Danan', title: 'CEO', email: 'rod.danan@prentus.com' },
      termStart: '2027-01-15',
      termEnd: '2029-01-14',
      billingStart: 'Contract Start Date',
      invoiceSchedule: 'Annually',
      paymentTerms: 'Due Upon Receipt',
      paymentMethods: 'ACH',
      marketingRights: ['quote', 'logo', 'case_study'],
      lines: [
        { id: 'of-line-licenses', category: 'license', name: 'Pro AI Jobseeker Licenses', description: 'Enables a member to access all AI tools and other features of the Prentus platform for the entirety of their job search. Licenses are only used when a member finishes onboarding and has a Jobseeking status.', quantity: 5000, unitPrice: 27, unitLabel: null },
        { id: 'of-line-sso', category: 'add_on', name: 'SSO', description: 'Single sign-on through the campus identity provider.', quantity: 5000, unitPrice: 2, unitLabel: '/license', discountType: 'amount', discountValue: 0.5 },
        { id: 'of-line-domain', category: 'add_on', name: 'Custom Domain', description: 'careers.summitstate.edu, maintained by Prentus.', quantity: 1, unitPrice: 2400, unitLabel: null },
        { id: 'of-line-interviews', category: 'add_on', name: 'AI Mock Interviews', description: 'Unlimited voice practice interviews with instant feedback.', quantity: 5000, unitPrice: 2, unitLabel: '/license' },
      ],
      discountEnabled: true,
      discountLabel: 'Two-year commitment discount',
      discountAmount: 13500,
      discountScope: 'licenses',
      additionalTerms: '## Overage Billing Rate\nAny License usage exceeding the quantity specified in this Order Form will be billed monthly at $27 per License, as described in Section 1.7(e) of the Cloud Service Agreement.\nLicenses not activated in year one carry into year two of the term.',
      includeDefaultCsa: true,
      shareSignedCopy: true,
      signing: null,
      signedAt: null,
    },
  ],
  prequalificationOnly: false,
  businessCase: [],
  members: [
    {
      id: 'member-rod',
      side: 'prentus',
      name: 'Rod Danan',
      title: 'Founder & CEO, Prentus',
      email: 'rod.danan@prentus.com',
      bookingUrl: 'https://cal.com/rod-danan',
      avatarUrl: '/images/team/rod-danan.webp',
    },
    {
      id: 'member-dana',
      side: 'customer',
      name: 'Dana Whitfield',
      title: 'AVP, Career & Professional Development',
      email: null,
      bookingUrl: null,
      avatarUrl: null,
    },
    {
      id: 'member-marcus',
      side: 'customer',
      name: 'Marcus Lee',
      title: 'Director of Career Services',
      email: null,
      bookingUrl: null,
      avatarUrl: null,
    },
  ],
  tasks: [],
  assets: [
    {
      id: 'success-call-august',
      sectionKey: 'recordings',
      type: 'video',
      title: 'Summit State monthly check-in',
      description: 'Reviewed activation progress by college, agreed to target the College of Business with an advisor-led campaign, and set the spring career fair as the next milestone.',
      url: 'https://fathom.video/share/sjixPQRjNdz9eUP4Zi9ePpMP9iaftgQB',
      source: 'fathom',
      metadata: { occurredAt: '2026-08-12T17:00:00.000Z' },
    },
    {
      id: 'success-call-july',
      sectionKey: 'recordings',
      type: 'video',
      title: 'Advisor workflow training',
      description: 'Walked the advising team through review queues, caseload views, and the weekly engagement report.',
      url: 'https://fathom.video/share/xyRRUSfMFN23pRDVnM85g8zzt_fr3Gd9',
      source: 'fathom',
      metadata: { occurredAt: '2026-07-15T16:00:00.000Z' },
    },
    {
      id: 'success-call-june',
      sectionKey: 'recordings',
      type: 'video',
      title: 'Quarterly business review',
      description: 'Reviewed Q2 activation and engagement, confirmed success metrics for the year, and aligned on the fall semester rollout plan.',
      url: 'https://fathom.video/share/wJf-dVweuNi8Sheg6TsFR23UJCmBjuLf',
      source: 'fathom',
      metadata: { occurredAt: '2026-06-18T18:30:00.000Z' },
    },
    {
      id: 'project-doc-expansion-plan',
      sectionKey: 'projects',
      type: 'pdf',
      title: 'College of Business rollout plan',
      description: 'Timeline, advisor assignments, and the invite plan for the expansion.',
      url: null,
      source: 'manual',
      metadata: { projectId: 'project-expansion' },
    },
    {
      id: 'renewal-doc-previous-contract',
      sectionKey: 'renewal',
      type: 'pdf',
      title: 'Current agreement (2026 term)',
      description: 'Your signed agreement for the current term, attached for reference.',
      url: null,
      source: 'manual',
    },
    ...STANDARD_WHATS_NEW_ASSETS,
    ...STANDARD_RESOURCE_ASSETS,
    ...STANDARD_PRODUCT_ASSETS,
    ...STANDARD_CASE_STUDY_ASSETS,
  ],
  calculatorInputs: {},
  viewerEmail: 'preview@summitstate.edu',
  updatedAt: '2026-08-21T12:00:00.000Z',
}

export const SUCCESS_DEMO_PUBLIC_WORKSPACE = publicWorkspace

export const SUCCESS_DEMO_ADMIN_WORKSPACE: AdminWorkspace = {
  ...publicWorkspace,
  status: 'published',
  companyHubspotId: SUCCESS_DEMO_PUBLIC_ID,
  deals: [{ id: 'success-demo-deal', name: 'Summit State University Partnership', stage: 'Closed Won' }],
  allowedDomains: ['summitstate.edu'],
  pricingVisibility: 'hidden',
  pendingApprovals: 0,
  suggestions: [],
  sandboxRequests: [],
  followUp: null,
  reviewWarnings: [],
  qualification: null,
  tasks: [],
  assets: publicWorkspace.assets.map((asset) => ({ ...asset, visibility: 'published' })),
  businessCase: [],
  timeline: [],
}
